/**
 * Design Templates — Learn-Mode inline tips.
 *
 * Covers `routes/social/TemplateBrowser.tsx`, `routes/social/Templates.tsx`
 * (saved post templates), and the `components/templates/*` pieces the
 * browser renders.
 */

import type { LearnTip } from "./types";

export const templatesTips: LearnTip[] = [
  {
    key: "template-browser",
    title: "Design Templates",
    body: "Start a new design from a professionally made template instead of a blank canvas. Preview any template in the selected client's brand before you commit to it.",
  },
  {
    key: "template-ai-suggest",
    title: "Describe Your Post Goal",
    body: "Describe what the post is for in plain language and get a ranked shortlist of templates that fit, instead of browsing the whole library.",
  },
  {
    key: "template-performance-sort",
    title: "Sorted by Performance",
    body: "Ranks templates by the real engagement of published posts made from them, compared with each account's typical post. A template needs enough published posts before it earns a performance badge.",
  },
  {
    key: "template-browser-filters",
    title: "Search & Filters",
    body: "Search the template library by keyword, change the sort order, or preview results rendered in the selected client's brand.",
  },
  {
    key: "template-brand-preview-toggle",
    title: "Preview in My Brand",
    body: "Shows every template with the selected client's colors, fonts, and logo already applied, so you can judge how it will actually look before you pick it.",
  },
  {
    key: "template-browser-rails",
    title: "Browse by Category",
    body: "Curated rows for quickly finding a template — by category, proven top performers, and what's trending this season.",
  },
  {
    key: "template-proven-rail",
    title: "Proven Performers",
    body: "Templates with a track record — the posts made from them have measurably outperformed a typical post for the accounts that used them.",
  },
  {
    key: "template-browser-grid",
    title: "Template Results",
    body: "Every template matching your current search and filters. Click one to preview it, then start a design from it.",
  },
  {
    key: "template-browser-no-results",
    title: "No Templates Found",
    body: "Nothing matched your current search and filters. Try one of the suggested alternatives, or clear a filter to widen the results.",
  },
  {
    key: "template-card",
    title: "Template Preview",
    body: "A thumbnail of this template — click it to open the full preview, or to start a new design from it.",
  },
  {
    key: "template-performance-badge",
    title: "Performance Badge",
    body: "This template has a measured track record: posts made from it have engaged noticeably better than this account's typical post.",
  },
  {
    key: "template-preview-modal",
    title: "Template Preview",
    body: "A closer look at this template before you use it — flip through its pages, and preview it in your client's brand if one is selected.",
  },
  {
    key: "template-preview-performance",
    title: "Performance",
    body: "How posts made from this exact template have performed compared to a typical post, based on real published results.",
  },
  {
    key: "post-templates-grid",
    title: "Saved Post Templates",
    body: "Your own reusable post templates — captions and settings you've saved to reuse, separate from the design template library. Use one to start a new post with it, edit it with the pencil, or delete any you no longer need.",
    bullets: [
      "Use — opens the composer with the template's caption, hashtags, networks and first comment filled in.",
      "Each use is counted, so you can see which templates earn their keep.",
    ],
  },
  {
    key: "post-templates-form",
    title: "Create or Edit a Template",
    body: "Give the template a name, the caption copy and any hashtags. You can also save a template straight from the composer with “Save as template”.",
  },
];
